import { useFormikContext } from "formik";
import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { FormContext } from "../../context/formContext";
import React from "react";
import Input from "../Input";
import CalendarComponent from "../CalendarComponent";
import CheckBoxComponent from "../CheckBoxComponent";
import DataTableComponent from "../DatatableComponent";
import DropdownComponent from "../DropdownComponent";
import EditorComponent from "../EditorComponent";
import FileUploadComponent from "../FileUploadComponent";
import CaptchaComponent from "../CaptchaComponent";
import { AdvisoryText } from "../../../AdvisoryText";
import SearchSelectComponent from "../SearchSelectComponent";
import ErrorComponent from "../ErrorComponent";
import ButtonComponent from "../ButtonComponent";
import InputTextareaComponent from "../InputTextAreaComponent";
import RadioButtonComponent from "../RadioButtonComponent";
import InputNumberComponent from "../InputNumberComponent";
import TimeComponent from "../TimeComponent";

export default function FieldComponent({
  parentKey,
  elementKey,
  extendedlabel,
  fieldType,
  label,
  parentvalues,
  hideExpression,
  calculatedValue,
  warn,
  rest,
  expressionProps,
  disabled, //inherited disabled property from a group or field
}: {
  parentKey: string;
  elementKey: string;
  fieldType: string;
  label?: string | ReactElement;
  extendedlabel?: string | ReactElement;
  parentvalues: any;
  hideExpression?: (formContext: any, values: any, model: any) => boolean;
  calculatedValue?: (formContext: any, values: any, model: any) => any;
  warn?: string | ReactElement;
  rest: any;
  expressionProps?: { [key: string]: (formContext: any, values: any, model: any, props: any) => any };
  disabled?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext();
  const context = useContext(FormContext);

  const Input = useMemo(() => {
    return matchedInput(fieldType);
  }, [fieldType]);

  const finalKey = useMemo(() => {
    return [parentKey, elementKey].filter((x) => x !== "" && x != null).join(".");
  }, [parentKey, elementKey]);

  const inputValue = useMemo(() => {
    const value = parentvalues[elementKey];
    return value;
  }, [elementKey, parentvalues]);

  const [resolvedExpressionProps, setResolvedExpressionProps] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    if (expressionProps != null) {
      const newProps: { [key: string]: any } = {};
      Object.keys(expressionProps).forEach((key) => {
        const res = expressionProps[key](context.formContextStore, values, parentvalues, rest);
        newProps[key] = res;
      });
      setResolvedExpressionProps(newProps);
    }

    if (hideExpression != null) {
      const isHidden = hideExpression(context.formContextStore, values, parentvalues);
      // Only update the state if `isHidden` differs from `hidden`
      if (isHidden !== parentvalues[elementKey + "Hidden"]) {
        setFieldValue(finalKey + "Hidden", isHidden);
      }
    }
    if (calculatedValue != null) {
      const newVal = calculatedValue(context.formContextStore, values, parentvalues);
      if (fieldType != "calendar") {
        if (
          ((rest == undefined || !rest?.isComplexValue) && newVal != parentvalues[elementKey]) ||
          (rest?.isComplexValue && JSON.stringify(newVal) != JSON.stringify(parentvalues[elementKey]))
        ) {
          setFieldValue(finalKey, newVal);
        }
      } else {
        let shedifferent = false;
        if (!parentvalues[elementKey] || parentvalues[elementKey] instanceof String) {
          shedifferent = newVal != parentvalues[elementKey];
        } else if (parentvalues[elementKey] instanceof Date) {
          const isostring = parentvalues[elementKey].toISOString();
          shedifferent = newVal + ".000Z" != isostring;
        }
        if (shedifferent) setFieldValue(finalKey, newVal);
      }
    }
  }, [expressionProps, hideExpression, values, parentvalues, context, rest]);

  return (
    <>
      {!parentvalues[elementKey + "Hidden"] && (
        <div id={"field_" + elementKey} className={"w-full generic-class " + (rest?.css ?? "") + (rest?.isRequired ? " isRequired" : "")}>
          <Input
            key={finalKey + "_" + elementKey}
            name={finalKey}
            value={inputValue}
            warn={warn}
            label={label}
            extendedlabel={extendedlabel}
            parentvalues={parentvalues}
            //leave last to override any other props
            {...{
              disabled: disabled || context.formContextStore.disabled,
              ...rest,
              ...resolvedExpressionProps,
            }}
          />
        </div>
      )}
    </>
  );
}

function matchedInput(fieldType: string): React.FC<any> {
  switch (fieldType) {
    case "input":
      return Input;

    case "textarea":
      return InputTextareaComponent;

    case "calendar":
      return CalendarComponent;

    case "checkbox":
      return CheckBoxComponent;

    case "datatable":
      return DataTableComponent;

    case "dropdown":
      return DropdownComponent;

    case "radio":
      return RadioButtonComponent;

    case "editor":
      return EditorComponent;

    case "file":
      return FileUploadComponent;

    case "captcha":
      return CaptchaComponent;

    case "advisory":
      return AdvisoryText;

    case "search":
      return SearchSelectComponent;

    case "error":
      return ErrorComponent;

    case "button":
      return ButtonComponent;

    case "number":
      return InputNumberComponent;

    case "time":
      return TimeComponent;

    default:
      return function Default() {
        return <></>;
      };
  }
}
